/**
 * Site search: one engine behind the search modal, the custom nav's panel
 * field and the athletes strip on /search.
 *
 * - Local sources answer on every keystroke, with no network: athletes
 *   (LiveHeats worker index via athlete-api.js, cached in memory and
 *   sessionStorage) and festivals (inlined in #site-search-data).
 * - Shopify results (products, collections, articles, pages, query
 *   suggestions) come from sections/search-suggestions.liquid through
 *   /search/suggest. Requests are debounced, aborted when superseded and
 *   cached per term, so backspacing is instant.
 * - Matching ignores accents, case and punctuation ("cedric" finds
 *   "Cédric", "reynolds madsen" finds "REYNOLDS-MADSEN"), ranks name
 *   starts first and tolerates one typo in words of 4+ letters.
 *
 * Docs: dev-logs/content/site-search.md
 */
import { fetchAllAthletes } from './athlete-api.js';

/* ── Shared data ─────────────────────────────────────────────────────────── */

const DATA = (() => {
  try {
    return JSON.parse(document.getElementById('site-search-data')?.textContent || '{}');
  } catch (e) {
    return {};
  }
})();
const T = DATA.i18n || {};
const ROUTES = window.Theme?.routes || {};
const ROOT = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
const SEARCH_URL = ROUTES.search_url || ROOT + 'search';
const SUGGEST_URL = ROUTES.predictive_search_url || ROOT + 'search/suggest';

const LIMITS = { queries: 4, athletes: 5, festivals: 3, products: 6, collections: 3, articles: 3, pages: 3 };
const DEBOUNCE_MS = 120;
const RECENT_KEY = 'site-search-recent';

/* ── Text folding + matching ─────────────────────────────────────────────── */

const MARKS = /[̀-ͯ]/g;
const NON_WORD = /[^\p{L}\p{N}]+/gu;

/** Lowercase, accent-free, punctuation-free, single-spaced. */
function toKey(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(MARKS, '')
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(NON_WORD, ' ')
    .trim();
}

/** Damerau (OSA) distance ≤ 1. */
function withinOne(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  while (i < la && i < lb && a[i] === b[i]) i++;
  if (la === lb) {
    if (a.slice(i + 1) === b.slice(i + 1)) return true; // substitution
    return a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2); // transposition
  }
  return la > lb ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1); // deletion / insertion
}

/**
 * A query token loosely matches a word: one typo against the word or its
 * same-length prefix. Only for 5+ letters with the first letter right, or
 * short tokens drag in noise ("hood" → "good", "nate" → "kate").
 */
function fuzzyWord(token, word) {
  if (token.length < 5 || token[0] !== word[0]) return false;
  for (let len = token.length - 1; len <= token.length + 1; len++) {
    if (len <= word.length && withinOne(token, word.slice(0, len))) return true;
  }
  return false;
}

/**
 * @param {{key: string, words: string[], extra: string[]}} entry
 * @param {string} q - query key
 * @param {string[]} tokens - query key split on spaces
 */
function score(entry, q, tokens) {
  const { key, words, extra } = entry;
  if (key === q) return 1000;
  if (key.startsWith(q)) return 800 - key.length;

  let allPrefix = true;
  let firstWord = false;
  for (let i = 0; i < tokens.length; i++) {
    const idx = words.findIndex((w) => w.startsWith(tokens[i]));
    if (idx === -1) {
      allPrefix = false;
      break;
    }
    if (i === 0 && idx === 0) firstWord = true;
  }
  if (allPrefix) return (firstWord ? 600 : 500) - key.length;

  if (q.length >= 3 && key.replace(/ /g, '').includes(q.replace(/ /g, ''))) return 300 - key.length;

  if (tokens.every((t) => words.some((w) => w.startsWith(t) || fuzzyWord(t, w)))) return 200 - key.length;

  if (extra.length && tokens.every((t) => extra.some((w) => w === t || (t.length >= 4 && w.startsWith(t))))) return 50;
  return 0;
}

function rank(entries, q, tokens, limit) {
  const hits = [];
  for (const entry of entries) {
    const s = score(entry, q, tokens);
    if (s > 0) hits.push([s + (entry.boost || 0), entry]);
  }
  hits.sort((a, b) => b[0] - a[0] || a[1].key.localeCompare(b[1].key));
  return hits.slice(0, limit).map((h) => h[1]);
}

function prepare(display, extraText) {
  const key = toKey(display);
  return { key, words: key.split(' '), extra: extraText ? toKey(extraText).split(' ').filter(Boolean) : [] };
}

/* ── Sources ─────────────────────────────────────────────────────────────── */

const nowSec = Date.now() / 1000;
const RAW_FESTIVALS = DATA.festivals || [];
// Words every festival name shares ("jackalope") can't tell festivals
// apart, so they don't count: "jack" shouldn't list every festival.
const SHARED_FEST_WORDS = (() => {
  const sets = RAW_FESTIVALS.map((f) => new Set(toKey(f.n).split(' ').filter(Boolean)));
  if (sets.length < 2) return new Set();
  return new Set([...sets[0]].filter((w) => sets.every((s) => s.has(w))));
})();
const FESTIVALS = RAW_FESTIVALS
  .map((f) => ({
    ...prepare(
      f.t,
      toKey(f.n)
        .split(' ')
        .filter((w) => !SHARED_FEST_WORDS.has(w))
        .join(' ')
    ),
    title: f.t,
    meta: f.d,
    url: f.u,
    image: f.i,
    upcoming: !f.e || f.e >= nowSec,
    start: f.s || 0,
    boost: !f.e || f.e >= nowSec ? 5 : 0,
  }))
  .sort((a, b) => (a.upcoming === b.upcoming ? (a.upcoming ? a.start - b.start : b.start - a.start) : a.upcoming ? -1 : 1));

function sizedAvatar(url, size) {
  if (!url) return '';
  try {
    const u = new URL(url, location.origin);
    if (u.hostname === 'cdn.shopify.com') {
      u.searchParams.set('width', size);
      u.searchParams.set('height', size);
      u.searchParams.set('crop', 'center');
      return u.toString();
    }
    if (u.hostname.endsWith('liveheats.com')) {
      u.pathname += '!width=' + size;
      return u.toString();
    }
  } catch (e) {
    /* fall through */
  }
  return url;
}

/** @type {Promise<any[]> | null} */
let athletesPromise = null;
/** @type {any[] | null} */
let athletes = null;
let athletesFailed = false;

function loadAthletes() {
  if (!athletesPromise) {
    athletesPromise = fetchAllAthletes()
      .then((list) => {
        // The index has duplicate people under different handles
        // ("BEN PATERSON" / "Ben Paterson", "MCCAULEY" / "Mc Cauley"):
        // keep one per name, preferring the one with a photo.
        const byName = new Map();
        athletes = [];
        for (const a of list || []) {
          const name = String(a.name || '').trim();
          if (!name || !a.url) continue;
          const image = a.local_image || a.image || '';
          const nameKey = toKey(name).replace(/ /g, '');
          const prev = byName.get(nameKey);
          if (prev && (prev.image || !image)) continue;
          if (prev) athletes.splice(athletes.indexOf(prev), 1);
          const entry = {
            ...prepare(name, `${a.country || ''} ${a.discipline || ''}`),
            title: name,
            meta: [a.country, a.discipline].filter(Boolean).join(' · '),
            url: a.url,
            image: image ? sizedAvatar(image, 96) : '',
            boost: image ? 3 : 0,
          };
          byName.set(nameKey, entry);
          athletes.push(entry);
        }
        return athletes;
      })
      .catch(() => {
        athletesPromise = null; // let the next focus retry
        athletesFailed = true; // …but don't hold back the "nothing found" hint meanwhile
        return [];
      });
  }
  return athletesPromise;
}

/** @type {Map<string, any>} */
const remoteCache = new Map();

function fetchRemote(q, signal) {
  const url = new URL(SUGGEST_URL, location.origin);
  url.searchParams.set('q', q);
  url.searchParams.set('section_id', 'search-suggestions');
  url.searchParams.set('resources[type]', 'product,collection,article,page,query');
  url.searchParams.set('resources[limit]', '6');
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('resources[options][prefix]', 'last');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  return fetch(url, { signal, headers: { Accept: 'text/html' } })
    .then((res) => {
      if (!res.ok) throw new Error(`search ${res.status}`);
      return res.text();
    })
    .then((text) => JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1)));
}

/* ── Recent searches (per-viewer convenience) ────────────────────────────── */

function getRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((s) => typeof s === 'string').slice(0, 5) : [];
  } catch (e) {
    return [];
  }
}

function saveRecent(term) {
  const clean = String(term || '').trim().slice(0, 80);
  if (toKey(clean).length < 2) return;
  try {
    const list = getRecent().filter((s) => toKey(s) !== toKey(clean));
    list.unshift(clean);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
  } catch (e) {
    /* storage unavailable */
  }
}

function clearRecent() {
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch (e) {
    /* storage unavailable */
  }
}

/* ── Rendering helpers ───────────────────────────────────────────────────── */

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const fill = (tpl, map) => String(tpl || '').replace(/__([A-Z])__/g, (m, k) => (k in map ? map[k] : m));

/** Escape `text` and wrap the parts that start a word matching a query token in <mark>. */
function highlight(text, tokens) {
  text = String(text || '');
  if (!tokens.length) return esc(text);
  let folded = '';
  const map = [];
  for (let i = 0; i < text.length; i++) {
    const f = text[i].normalize('NFD').replace(MARKS, '').toLowerCase();
    for (const ch of f) {
      folded += ch;
      map.push(i);
    }
  }
  const marked = new Array(text.length).fill(false);
  for (const t of tokens) {
    const re = new RegExp('(^|[^\\p{L}\\p{N}])(' + escRe(t) + ')', 'gu');
    let m;
    while ((m = re.exec(folded))) {
      const start = m.index + m[1].length;
      for (let j = start; j < start + t.length; j++) marked[map[j]] = true;
      if (re.lastIndex === m.index) re.lastIndex++;
    }
  }
  let out = '';
  let open = false;
  for (let i = 0; i < text.length; i++) {
    if (marked[i] && !open) (out += '<mark>'), (open = true);
    if (!marked[i] && open) (out += '</mark>'), (open = false);
    out += esc(text[i]);
  }
  return open ? out + '</mark>' : out;
}

function searchHref(q) {
  const url = new URL(SEARCH_URL, location.origin);
  url.searchParams.set('q', q);
  url.searchParams.set('options[prefix]', 'last');
  return url.pathname + url.search;
}

/* ── <site-search> ───────────────────────────────────────────────────────── */

let uid = 0;

class SiteSearch extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.variant = this.dataset.variant || 'modal';
    this.form = this.querySelector('[data-site-search-form]');
    this.input = this.querySelector('[data-site-search-input]');
    this.results = this.querySelector('[data-site-search-results]');
    this.status = this.querySelector('[data-site-search-status]');
    this.clearBtn = this.querySelector('[data-site-search-clear]');
    this.idBase = `ss${++uid}`;
    this.qKey = '';
    this.remote = null;
    this.remoteKey = '';
    this.timer = 0;
    this.controller = null;
    if (!this.form || !this.input || !this.results) return;

    this.input.addEventListener('input', () => this.update());
    this.input.addEventListener('focus', () => this.warm(), { passive: true });
    this.input.addEventListener('pointerdown', () => this.warm(), { passive: true });
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.clearBtn?.addEventListener('click', () => this.clear(true));
    this.results.addEventListener('click', (e) => this.onResultClick(e));
    this.results.addEventListener('mousemove', (e) => this.onHover(e), { passive: true });
    // Phones: dragging the results drops the keyboard so the list is usable.
    this.results.addEventListener(
      'touchmove',
      () => {
        if (document.activeElement === this.input) this.input.blur();
      },
      { passive: true }
    );

    if (this.variant === 'modal') {
      const dialog = this.closest('dialog-component');
      this.dialog = dialog;
      this.querySelector('[data-site-search-close]')?.addEventListener('click', () => dialog?.closeDialog?.());
      dialog?.addEventListener('dialog:open', () => {
        this.warm();
        requestAnimationFrame(() => {
          this.input.focus({ preventScroll: true });
          this.input.select();
        });
        if (!this.qKey) this.renderEmpty();
      });
    }
  }

  warm() {
    if (!athletes) loadAthletes();
  }

  get query() {
    return this.input.value.trim();
  }

  clear(focus) {
    this.input.value = '';
    this.update();
    if (focus) this.input.focus();
  }

  update() {
    const q = this.query;
    const qKey = toKey(q);
    this.qKey = qKey;
    if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
    this.toggleAttribute('data-query', q.length > 0);
    clearTimeout(this.timer);

    if (!qKey) {
      this.controller?.abort();
      this.removeAttribute('data-loading');
      this.renderEmpty();
      return;
    }

    // Shopify results: cached → instant; otherwise keep the last ones on
    // screen while the user is still editing the same term (no flicker).
    const cached = remoteCache.get(qKey);
    if (cached) {
      this.remote = cached;
      this.remoteKey = qKey;
      this.removeAttribute('data-loading');
    } else {
      if (!(this.remoteKey && (qKey.startsWith(this.remoteKey) || this.remoteKey.startsWith(qKey)))) {
        this.remote = null;
        this.remoteKey = '';
      }
      this.setAttribute('data-loading', '');
      this.timer = setTimeout(() => this.request(q, qKey), DEBOUNCE_MS);
    }

    if (!athletes && qKey.length >= 2) {
      loadAthletes().then(() => {
        if (this.qKey === qKey) this.render();
      });
    }
    this.render();
  }

  request(q, qKey) {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    fetchRemote(q, controller.signal)
      .then((data) => {
        remoteCache.set(qKey, data);
        if (remoteCache.size > 60) remoteCache.delete(remoteCache.keys().next().value);
        if (this.qKey !== qKey) return;
        this.remote = data;
        this.remoteKey = qKey;
        this.removeAttribute('data-loading');
        this.render();
      })
      .catch(() => {
        if (controller.signal.aborted || this.qKey !== qKey) return;
        this.removeAttribute('data-loading');
        this.remote = null;
        this.remoteKey = '';
        this.render();
      });
  }

  /* ── Views ── */

  renderEmpty() {
    clearTimeout(this.announceTimer);
    if (this.status) this.status.textContent = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    if (this.variant !== 'modal') {
      this.results.innerHTML = '';
      return;
    }
    let html = '';
    const recent = getRecent();
    if (recent.length) {
      html += this.group(
        'recent',
        T.recent,
        `<div class="site-search__chips">${recent
          .map((s) => `<a class="site-search__chip" role="option" href="${esc(searchHref(s))}" data-fill="${esc(s)}">${esc(s)}</a>`)
          .join('')}</div>`,
        `<button type="button" class="site-search__group-action" data-clear-recent>${esc(T.clearRecent)}</button>`
      );
    }
    if (FESTIVALS.length) html += this.group('festivals', T.festivals, FESTIVALS.map((f) => this.row(f, [], 'festival')).join(''));
    this.results.innerHTML = html;
    this.indexOptions();
  }

  render() {
    const q = this.query;
    const qKey = this.qKey;
    const tokens = qKey.split(' ').filter(Boolean);
    const r = this.remote || {};
    let html = '';
    let count = 0;

    const queries = (r.queries || []).filter((s) => toKey(s.t) !== qKey).slice(0, LIMITS.queries);
    if (queries.length) {
      html += this.group(
        'suggestions',
        T.suggestions,
        `<div class="site-search__chips">${queries
          .map((s) => `<a class="site-search__chip" role="option" href="${esc(searchHref(s.t))}" data-fill="${esc(s.t)}">${highlight(s.t, tokens)}</a>`)
          .join('')}</div>`
      );
    }

    if (qKey.length >= 2 && athletes) {
      const hits = rank(athletes, qKey, tokens, LIMITS.athletes);
      count += hits.length;
      if (hits.length) html += this.group('athletes', T.athletes, hits.map((a) => this.row(a, tokens, 'athlete')).join(''));
    }

    const fests = rank(FESTIVALS, qKey, tokens, LIMITS.festivals);
    count += fests.length;
    if (fests.length) html += this.group('festivals', T.festivals, fests.map((f) => this.row(f, tokens, 'festival')).join(''));

    const remoteGroups = [
      ['products', T.products, 'product'],
      ['collections', T.collections, 'collection'],
      ['articles', T.stories, 'article'],
      ['pages', T.pages, 'page'],
    ];
    for (const [key, label, kind] of remoteGroups) {
      const items = (r[key] || []).slice(0, LIMITS[key]);
      count += items.length;
      if (!items.length) continue;
      html += this.group(
        key,
        label,
        items
          .map((it) =>
            this.row(
              {
                title: it.t,
                url: it.u,
                image: it.i,
                meta: kind === 'product' ? (it.a === false ? T.soldOut : it.k) : it.d,
                price: it.p,
                compare: it.c,
              },
              tokens,
              kind
            )
          )
          .join('')
      );
    }

    const loading = this.hasAttribute('data-loading');
    if (!count && !queries.length && !loading && (athletes || athletesFailed || qKey.length < 2)) {
      html += `<p class="site-search__empty">${esc(fill(T.noResults, { Q: q }))}</p>`;
    }
    html += `<a class="site-search__all" role="option" href="${esc(searchHref(q))}">${esc(fill(T.viewAll, { Q: q }))}<span aria-hidden="true">→</span></a>`;

    this.results.innerHTML = html;
    this.input.setAttribute('aria-expanded', 'true');
    this.indexOptions();
    if (!loading) this.announce(count);
  }

  group(key, label, body, action = '') {
    const id = `${this.idBase}-g-${key}`;
    return `<div class="site-search__group site-search__group--${key}" role="group" aria-labelledby="${id}">
      <div class="site-search__group-head"><p class="site-search__group-title" id="${id}">${esc(label)}</p>${action}</div>
      ${body}
    </div>`;
  }

  row(item, tokens, kind) {
    const round = kind === 'athlete';
    const thumb = item.image
      ? `<img src="${esc(item.image)}" alt="" width="48" height="48" loading="lazy" decoding="async">`
      : `<span aria-hidden="true">${esc((item.title || '?').trim().charAt(0))}</span>`;
    const price = item.price
      ? `<span class="site-search__price">${item.compare ? `<s>${esc(item.compare)}</s> ` : ''}${esc(item.price)}</span>`
      : '';
    const thumbHtml = kind === 'page' ? '' : `<span class="site-search__thumb${round ? ' site-search__thumb--round' : ''}">${thumb}</span>`;
    return `<a class="site-search__item site-search__item--${kind}" role="option" href="${esc(item.url)}">
      ${thumbHtml}
      <span class="site-search__text">
        <span class="site-search__title">${highlight(item.title, tokens)}</span>
        ${item.meta ? `<span class="site-search__meta">${esc(item.meta)}</span>` : ''}
      </span>
      ${price}
    </a>`;
  }

  indexOptions() {
    this.options = [...this.results.querySelectorAll('[role="option"]')];
    this.options.forEach((el, i) => (el.id = `${this.idBase}-o${i}`));
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
  }

  setActive(i, scroll = true) {
    const opts = this.options || [];
    if (this.active >= 0) opts[this.active]?.classList.remove('is-active');
    this.active = i;
    const el = opts[i];
    if (!el) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    el.classList.add('is-active');
    this.input.setAttribute('aria-activedescendant', el.id);
    if (scroll) el.scrollIntoView({ block: 'nearest' });
  }

  announce(count) {
    if (!this.status) return;
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.status.textContent = fill(count === 1 ? T.countOne : T.countOther, { N: count });
    }, 500);
  }

  /* ── Events ── */

  onKeydown(e) {
    const opts = this.options || [];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!opts.length) return;
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const next = this.active < 0 ? (dir > 0 ? 0 : opts.length - 1) : (this.active + dir + opts.length) % opts.length;
      this.setActive(next);
    } else if (e.key === 'Enter' && this.active >= 0 && opts[this.active]) {
      e.preventDefault();
      this.activate(opts[this.active]);
    } else if (e.key === 'Escape' && this.input.value) {
      // First Esc clears the field; the next one closes the modal / menu.
      e.preventDefault();
      e.stopPropagation();
      this.clear(true);
    }
  }

  onHover(e) {
    const el = e.target.closest?.('[role="option"]');
    const i = el ? (this.options || []).indexOf(el) : -1;
    if (i >= 0 && i !== this.active) this.setActive(i, false);
  }

  onResultClick(e) {
    if (e.target.closest('[data-clear-recent]')) {
      clearRecent();
      this.renderEmpty();
      this.input.focus();
      return;
    }
    const el = e.target.closest('a[role="option"]');
    if (!el || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    if (el.dataset.fill != null) {
      e.preventDefault();
      this.activate(el);
      return;
    }
    saveRecent(this.query);
  }

  activate(el) {
    if (el.dataset.fill != null) {
      this.input.value = el.dataset.fill;
      this.update();
      this.input.focus();
      return;
    }
    saveRecent(this.query);
    location.href = el.href;
  }

  onSubmit(e) {
    if (!this.query) {
      e.preventDefault();
      return;
    }
    saveRecent(this.query);
  }
}

if (!customElements.get('site-search')) customElements.define('site-search', SiteSearch);

/* ── Keyboard shortcuts: Cmd/Ctrl+K and "/" open the modal ───────────────── */

document.addEventListener('keydown', (e) => {
  const modal = document.getElementById('search-modal');
  if (!modal || typeof modal.showDialog !== 'function') return;
  const typing = e.target instanceof HTMLElement && e.target.closest('input, textarea, select, [contenteditable="true"]');
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    modal.toggleDialog?.();
  } else if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
    e.preventDefault();
    modal.showDialog();
  }
});

/* ── /search page: athletes + festivals strip above the native results ──── */

function initPageStrip() {
  const host = document.querySelector('[data-site-search-page]');
  if (!host) return;
  const q = (host.dataset.q || '').replace(/\*$/, '').trim();
  const qKey = toKey(q);
  if (qKey.length < 2) return;
  const tokens = qKey.split(' ');

  loadAthletes().then((list) => {
    const hits = [...rank(FESTIVALS, qKey, tokens, 4), ...rank(list, qKey, tokens, 16)];
    if (!hits.length) return;
    host.innerHTML = `<h2 class="site-search-page__title h5">${esc(host.dataset.title || '')}</h2>
      <ul class="site-search-page__list" role="list">${hits
        .map(
          (h) => `<li><a class="site-search-page__card" href="${esc(h.url)}">
            <span class="site-search__thumb site-search__thumb--round">${
              h.image ? `<img src="${esc(h.image)}" alt="" width="96" height="96" loading="lazy">` : `<span aria-hidden="true">${esc(h.title.charAt(0))}</span>`
            }</span>
            <span class="site-search__title">${highlight(h.title, tokens)}</span>
            ${h.meta ? `<span class="site-search__meta">${esc(h.meta)}</span>` : ''}
          </a></li>`
        )
        .join('')}</ul>`;
    host.hidden = false;

    // No product/page matches: Shopify then shows the fallback collection
    // and a "no results" line. With athlete/festival hits, both are noise.
    if (host.dataset.native === '0') {
      document.querySelectorAll('[data-search-fallback], .search-results__no-results').forEach((el) => (el.hidden = true));
    }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPageStrip);
else initPageStrip();
